import type { Account } from '@/sync/account';
import { AmbiguousAuthError } from '@/sync/api-client';
import { showConfirm } from '@/editor/editor-dialogs';
import { toast } from '@/ui/toast';

type View =
  | 'signed_out'
  | 'signup_name'
  | 'signup_credentials'
  | 'signin'
  | 'recover'
  | 'signed_in'
  | 'rotate_recovery_pin'
  | 'credentials_reveal_only';

interface RevealState {
  title: string;
  pin?: string;
  recoveryCode?: string;
  /** Shown on signup reveal so the user knows their public identity tag. */
  displayName?: string;
  discriminator?: string;
}

export class AccountScreen {
  private element: HTMLElement | null = null;
  private view: View = 'signed_out';
  private reveal: RevealState | null = null;
  private suggestions: string[] = [];
  private nonce: string = '';
  private selectedName: string | null = null;
  private revealCheckboxChecked = false;
  private errorMessage: string = '';
  /** When the server says "ambiguous", the next submit also sends the tag. */
  private needsDiscriminator = false;

  onBack: (() => void) | null = null;

  constructor(private account: Account) {}

  show(): void {
    this.view = this.account.isSignedIn() ? 'signed_in' : 'signed_out';
    this.render();
  }

  hide(): void {
    if (this.element) { this.element.remove(); this.element = null; }
  }

  private render(): void {
    if (this.element) this.element.remove();
    const el = document.createElement('div');
    el.id = 'account-screen';
    el.style.cssText = `
      position: fixed; inset: 0;
      background: rgba(10, 10, 26, 0.95);
      display: flex; flex-direction: column;
      align-items: center; padding: 3vh 16px;
      z-index: 60; backdrop-filter: blur(8px);
      font-family: 'Courier New', monospace; color: #ccc;
      overflow-y: auto;
    `;

    const html = this.renderView();
    el.innerHTML = `
      <style>
        #account-screen h1 { color:#00ffff; font-size:clamp(1.2rem,4vw,1.6rem);
          font-weight:bold; text-shadow:0 0 20px #00ffff; letter-spacing:0.15em;
          margin:0 0 1rem; }
        #account-screen .acc-btn { min-height:44px; padding:10px 20px; font:inherit;
          background:#00ffff22; border:1px solid #00ffff; color:#00ffff; border-radius:4px; cursor:pointer; }
        #account-screen .acc-btn:disabled { opacity:0.4; cursor:not-allowed; }
        #account-screen .acc-btn-secondary { background:#111; border:1px solid #333; color:#aaa; }
        #account-screen .acc-btn-danger { background:#111; border:1px solid #ff4444; color:#ff6666; }
        #account-screen .acc-field { display:flex; flex-direction:column; gap:4px; width:100%; max-width:280px; }
        #account-screen .acc-field label { color:#888; font-size:0.75rem; }
        #account-screen .acc-field input { background:#111; color:#fff; border:1px solid #333;
          padding:10px; font:inherit; border-radius:4px; min-height:44px; }
        #account-screen .acc-link { color:#00ffff99; font-size:0.75rem; cursor:pointer;
          text-decoration:underline; background:none; border:none; padding:6px; }
        #account-screen .acc-card { background:rgba(0,255,255,0.03); border:1px solid #00ffff44;
          border-radius:6px; padding:12px; cursor:pointer; text-align:center; color:#00ffff; }
        #account-screen .acc-card.selected { background:rgba(0,255,255,0.15); border-color:#00ffff;
          color:#fff; box-shadow:0 0 10px #00ffff44; }
        #account-screen .acc-reveal { background:#111; border:1px solid #333; border-radius:8px;
          padding:12px; width:100%; max-width:320px; }
        #account-screen .acc-warn { background:#ff660022; border:1px solid #ff660066;
          color:#ff8800; padding:10px; border-radius:6px; width:100%; max-width:320px; text-align:center; font-size:0.8rem; }
        #account-screen .acc-error { color:#ff6666; font-size:0.8rem; min-height:1.2em; }
        #account-screen .acc-col { display:flex; flex-direction:column; align-items:center; gap:12px; width:100%; max-width:320px; }
      </style>
      ${html}
    `;

    document.body.appendChild(el);
    this.element = el;
    this.wireEvents(el);
  }

  private renderView(): string {
    switch (this.view) {
      case 'signed_out': return this.renderSignedOut();
      case 'signed_in':  return this.renderSignedIn();
      case 'signup_name': return this.renderSignupName();
      case 'signup_credentials': return this.renderCredentialsReveal(true);
      case 'credentials_reveal_only': return this.renderCredentialsReveal(false);
      case 'signin': return this.renderSignin();
      case 'recover': return this.renderRecover();
      case 'rotate_recovery_pin': return this.renderRotateRecoveryPin();
      default: return this.renderSignedOut();
    }
  }

  private renderSignedOut(): string {
    return `
      <h1>ACCOUNT</h1>
      <div class="acc-col">
        <button class="acc-btn" data-act="go-signup">Create Account</button>
        <button class="acc-btn acc-btn-secondary" data-act="go-signin">Sign In</button>
        <button class="acc-link" data-act="go-recover">Forgot PIN? Recover account</button>
        <button class="acc-btn acc-btn-secondary" data-act="back" style="margin-top:1rem">BACK</button>
      </div>
    `;
  }

  private renderSignedIn(): string {
    const sess = this.account.getSession();
    return `
      <h1>SIGNED IN</h1>
      <div class="acc-col">
        <div style="color:#fff;font-size:1.3rem">${escape(sess?.displayName ?? '')}</div>
        <div style="color:#555;font-size:0.8rem">#${escape(sess?.discriminator ?? '')}</div>
        <button class="acc-btn acc-btn-secondary" data-act="rotate-pin">Change PIN</button>
        <button class="acc-btn acc-btn-secondary" data-act="rotate-recovery">Change Recovery Code</button>
        <button class="acc-btn acc-btn-danger" data-act="logout">Sign Out</button>
        <button class="acc-btn acc-btn-secondary" data-act="back" style="margin-top:1rem">BACK</button>
      </div>
    `;
  }

  private renderSignupName(): string {
    const cards = this.suggestions.map((s) =>
      `<div class="acc-card ${this.selectedName === s ? 'selected' : ''}" data-act="pick-name" data-name="${escape(s)}">${escape(s)}</div>`
    ).join('');
    return `
      <h1>CREATE ACCOUNT</h1>
      <div style="color:#888;font-size:0.85rem;text-align:center;max-width:300px;margin-bottom:12px">
        Pick a name. Tap to select, or shuffle for more.
      </div>
      <div class="acc-col">
        ${cards}
        <div style="display:flex;gap:10px;margin-top:8px">
          <button class="acc-btn acc-btn-secondary" data-act="signup-shuffle">↻ SHUFFLE</button>
          <button class="acc-btn" data-act="signup-next" ${this.selectedName ? '' : 'disabled'}>NEXT →</button>
        </div>
        <div class="acc-error">${escape(this.errorMessage)}</div>
        <button class="acc-btn acc-btn-secondary" data-act="back-to-signed-out" style="margin-top:1rem">BACK</button>
      </div>
    `;
  }

  private renderCredentialsReveal(_isSignup: boolean): string {
    const pin = this.reveal?.pin ?? '';
    const rec = this.reveal?.recoveryCode ?? '';
    const title = this.reveal?.title ?? 'WELCOME';
    const displayName = this.reveal?.displayName ?? '';
    const discriminator = this.reveal?.discriminator ?? '';
    const identityCombined = displayName && discriminator ? `${displayName} #${discriminator}` : '';
    const identityBlock = identityCombined ? `
      <div class="acc-reveal">
        <div style="color:#888;font-size:0.7rem;margin-bottom:4px">YOUR IDENTITY</div>
        <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap">
          <div style="color:#fff;font-size:1.05rem;font-weight:bold">${escape(identityCombined)}</div>
          <button class="acc-btn" data-act="copy" data-copy="${escape(identityCombined)}" style="padding:4px 10px;min-height:32px;font-size:0.7rem">COPY</button>
        </div>
        <div style="color:#666;font-size:0.7rem;margin-top:4px">Others see this on levels you share.</div>
      </div>` : '';
    const pinBlock = pin ? `
      <div class="acc-reveal">
        <div style="color:#888;font-size:0.7rem;margin-bottom:4px">YOUR PIN</div>
        <div style="display:flex;align-items:center;gap:8px">
          <div style="color:#fff;font-size:1.4rem;letter-spacing:0.3em;font-weight:bold">${escape(pin)}</div>
          <button class="acc-btn" data-act="copy" data-copy="${escape(pin)}" style="padding:4px 10px;min-height:32px;font-size:0.7rem">COPY</button>
        </div>
      </div>` : '';
    const recBlock = rec ? `
      <div class="acc-reveal">
        <div style="color:#888;font-size:0.7rem;margin-bottom:4px">RECOVERY CODE</div>
        <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap">
          <div style="color:#fff;font-size:0.95rem;word-break:break-all">${escape(rec)}</div>
          <button class="acc-btn" data-act="copy" data-copy="${escape(rec)}" style="padding:4px 10px;min-height:32px;font-size:0.7rem">COPY</button>
        </div>
      </div>` : '';
    return `
      <h1>${escape(title)}</h1>
      <div class="acc-col">
        ${identityBlock}
        ${pinBlock}
        ${recBlock}
        <div class="acc-warn">⚠ Save these now! Take a screenshot or copy them somewhere safe. You won't see them again.</div>
        <label style="display:flex;align-items:center;gap:8px;color:#aaa;font-size:0.85rem;cursor:pointer">
          <input type="checkbox" data-act="reveal-checkbox" style="accent-color:#00ffff;width:18px;height:18px"
                 ${this.revealCheckboxChecked ? 'checked' : ''}>
          I've saved my credentials
        </label>
        <button class="acc-btn" data-act="reveal-continue" ${this.revealCheckboxChecked ? '' : 'disabled'}>CONTINUE</button>
      </div>
    `;
  }

  private renderSignin(): string {
    const discField = this.needsDiscriminator ? `
      <div class="acc-field">
        <label>TAG (e.g. 0427)</label>
        <input id="signin-disc" type="text" autocomplete="off" style="max-width:120px">
      </div>
      <div style="color:#ff8800;font-size:0.75rem;max-width:280px;text-align:center">
        Another account uses that name and PIN. Enter your 4-digit tag to sign in.
      </div>` : '';
    return `
      <h1>SIGN IN</h1>
      <div class="acc-col">
        <div class="acc-field">
          <label>DISPLAY NAME</label>
          <input id="signin-name" type="text" autocomplete="off">
        </div>
        <div class="acc-field">
          <label>PIN</label>
          <input id="signin-pin" type="password" autocomplete="off" style="max-width:150px;letter-spacing:0.3em">
        </div>
        ${discField}
        <button class="acc-btn" data-act="signin-submit">SIGN IN</button>
        <div class="acc-error">${escape(this.errorMessage)}</div>
        <button class="acc-link" data-act="go-recover">Forgot PIN? Recover account</button>
        <button class="acc-btn acc-btn-secondary" data-act="back-to-signed-out" style="margin-top:1rem">BACK</button>
      </div>
    `;
  }

  private renderRecover(): string {
    const discField = this.needsDiscriminator ? `
      <div class="acc-field">
        <label>TAG (e.g. 0427)</label>
        <input id="recover-disc" type="text" autocomplete="off" style="max-width:120px">
      </div>
      <div style="color:#ff8800;font-size:0.75rem;max-width:280px;text-align:center">
        Another account uses that name and recovery code. Enter your 4-digit tag to continue.
      </div>` : '';
    return `
      <h1>RECOVER ACCOUNT</h1>
      <div class="acc-col">
        <div class="acc-field">
          <label>DISPLAY NAME</label>
          <input id="recover-name" type="text" autocomplete="off">
        </div>
        <div class="acc-field">
          <label>RECOVERY CODE (5 words, hyphen-separated)</label>
          <input id="recover-code" type="text" autocomplete="off">
        </div>
        ${discField}
        <button class="acc-btn" data-act="recover-submit">RECOVER</button>
        <div class="acc-error">${escape(this.errorMessage)}</div>
        <button class="acc-btn acc-btn-secondary" data-act="back-to-signed-out" style="margin-top:1rem">BACK</button>
      </div>
    `;
  }

  private renderRotateRecoveryPin(): string {
    return `
      <h1>CONFIRM PIN</h1>
      <div class="acc-col">
        <div style="color:#888;font-size:0.85rem;text-align:center;max-width:300px">
          Enter your current PIN to get a new recovery code.
        </div>
        <div class="acc-field">
          <label>PIN</label>
          <input id="rotrec-pin" type="password" autocomplete="off" style="max-width:150px;letter-spacing:0.3em">
        </div>
        <button class="acc-btn" data-act="rotrec-submit">CONFIRM</button>
        <div class="acc-error">${escape(this.errorMessage)}</div>
        <button class="acc-btn acc-btn-secondary" data-act="back-to-signed-in" style="margin-top:1rem">CANCEL</button>
      </div>
    `;
  }

  private wireEvents(root: HTMLElement): void {
    root.querySelectorAll<HTMLElement>('[data-act]').forEach((btn) => {
      const eventType = btn.tagName === 'INPUT' && (btn as HTMLInputElement).type === 'checkbox' ? 'change' : 'pointerdown';
      btn.addEventListener(eventType, async (e) => {
        e.stopPropagation();
        const act = btn.dataset.act!;
        await this.handleAction(act, btn);
      });
    });
  }

  private async handleAction(act: string, btn?: HTMLElement): Promise<void> {
    this.errorMessage = '';
    switch (act) {
      case 'back':
        this.hide();
        this.onBack?.();
        return;
      case 'back-to-signed-out':
        this.view = 'signed_out';
        this.render();
        return;
      case 'back-to-signed-in':
        this.view = 'signed_in';
        this.render();
        return;

      case 'go-signup':
        try {
          const r = await this.account.getSuggestions(5);
          this.suggestions = r.suggestions;
          this.nonce = r.nonce;
          this.selectedName = null;
          this.view = 'signup_name';
          this.render();
        } catch (e) {
          this.errorMessage = humanError(e);
          this.render();
        }
        return;

      case 'signup-shuffle': {
        try {
          const r = await this.account.getSuggestions(5);
          this.suggestions = r.suggestions;
          this.nonce = r.nonce;
          this.selectedName = null;
          this.render();
        } catch (e) {
          this.errorMessage = humanError(e);
          this.render();
        }
        return;
      }

      case 'pick-name': {
        const name = btn?.dataset.name ?? '';
        this.selectedName = name;
        // Update cards and next button in place to preserve DOM references held by tests.
        this.element?.querySelectorAll<HTMLElement>('.acc-card').forEach((card) => {
          card.classList.toggle('selected', card.dataset.name === name);
        });
        const nextBtn = this.element?.querySelector<HTMLButtonElement>('[data-act="signup-next"]');
        if (nextBtn) nextBtn.disabled = !this.selectedName;
        return;
      }

      case 'signup-next': {
        if (!this.selectedName) return;
        try {
          const r = await this.account.signup(this.selectedName, this.nonce);
          this.reveal = {
            title: `WELCOME, ${r.displayName}`,
            displayName: r.displayName,
            discriminator: r.discriminator,
            pin: r.pin,
            recoveryCode: r.recoveryCode,
          };
          this.revealCheckboxChecked = false;
          this.view = 'signup_credentials';
          this.render();
        } catch (e) {
          this.errorMessage = humanError(e);
          this.render();
        }
        return;
      }

      case 'reveal-checkbox': {
        // Toggle CONTINUE button state in place — no full re-render so focus
        // and the checkbox's own state (captured by the user click) survive.
        const cb = this.element?.querySelector<HTMLInputElement>('[data-act="reveal-checkbox"]');
        this.revealCheckboxChecked = !!cb?.checked;
        const cont = this.element?.querySelector<HTMLButtonElement>('[data-act="reveal-continue"]');
        if (cont) cont.disabled = !this.revealCheckboxChecked;
        return;
      }

      case 'reveal-continue':
        this.reveal = null;
        this.revealCheckboxChecked = false;
        this.view = 'signed_in';
        this.render();
        return;

      case 'copy': {
        const text = btn?.dataset.copy ?? '';
        await copyToClipboard(text);
        return;
      }

      case 'go-signin':
        this.needsDiscriminator = false;
        this.view = 'signin';
        this.render();
        return;

      case 'signin-submit': {
        const name = (this.element!.querySelector('#signin-name') as HTMLInputElement).value.trim();
        const pin  = (this.element!.querySelector('#signin-pin') as HTMLInputElement).value.trim();
        const discEl = this.element!.querySelector('#signin-disc') as HTMLInputElement | null;
        const disc = discEl?.value.trim() || undefined;
        try {
          await this.account.signin(name, pin, disc);
          this.needsDiscriminator = false;
          this.view = 'signed_in';
          this.render();
        } catch (e) {
          if (e instanceof AmbiguousAuthError) {
            this.needsDiscriminator = true;
            this.errorMessage = '';
          } else {
            this.errorMessage = humanError(e);
          }
          this.render();
        }
        return;
      }

      case 'go-recover':
        this.needsDiscriminator = false;
        this.view = 'recover';
        this.render();
        return;

      case 'recover-submit': {
        const name = (this.element!.querySelector('#recover-name') as HTMLInputElement).value.trim();
        const code = (this.element!.querySelector('#recover-code') as HTMLInputElement).value.trim();
        const discEl = this.element!.querySelector('#recover-disc') as HTMLInputElement | null;
        const disc = discEl?.value.trim() || undefined;
        try {
          const r = await this.account.recover(name, code, disc);
          this.reveal = {
            title: 'NEW PIN',
            displayName: r.displayName,
            discriminator: r.discriminator,
            pin: r.pin,
          };
          this.needsDiscriminator = false;
          this.revealCheckboxChecked = false;
          this.view = 'credentials_reveal_only';
          this.render();
        } catch (e) {
          if (e instanceof AmbiguousAuthError) {
            this.needsDiscriminator = true;
            this.errorMessage = '';
          } else {
            this.errorMessage = humanError(e);
          }
          this.render();
        }
        return;
      }

      case 'rotate-pin': {
        try {
          const r = await this.account.rotatePin();
          const sess = this.account.getSession();
          this.reveal = {
            title: 'NEW PIN',
            displayName: sess?.displayName,
            discriminator: sess?.discriminator,
            pin: r.pin,
          };
          this.revealCheckboxChecked = false;
          this.view = 'credentials_reveal_only';
          this.render();
        } catch (e) {
          this.errorMessage = humanError(e);
          this.render();
        }
        return;
      }

      case 'rotate-recovery':
        this.view = 'rotate_recovery_pin';
        this.render();
        return;

      case 'rotrec-submit': {
        const pin = (this.element!.querySelector('#rotrec-pin') as HTMLInputElement).value.trim();
        try {
          const r = await this.account.rotateRecovery(pin);
          const sess = this.account.getSession();
          this.reveal = {
            title: 'NEW RECOVERY CODE',
            displayName: sess?.displayName,
            discriminator: sess?.discriminator,
            recoveryCode: r.recoveryCode,
          };
          this.revealCheckboxChecked = false;
          this.view = 'credentials_reveal_only';
          this.render();
        } catch (e) {
          this.errorMessage = humanError(e);
          this.render();
        }
        return;
      }

      case 'logout': {
        const ok = await showConfirm('Sign Out', 'Are you sure you want to sign out?', 'Sign Out');
        if (!ok) return;
        await this.account.logout();
        this.view = 'signed_out';
        this.render();
        return;
      }
    }
  }
}

function escape(s: string): string {
  return s.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]!));
}

async function copyToClipboard(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    toast('Copied', { durationMs: 1200, variant: 'success' });
  } catch {
    // Fallback: select a temporary element
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    try {
      document.execCommand('copy');
      toast('Copied', { durationMs: 1200, variant: 'success' });
    } catch {
      toast('Copy failed — select and copy manually', { variant: 'error' });
    }
    ta.remove();
  }
}

function humanError(e: unknown): string {
  if (e instanceof Error) {
    if (e.message.includes('invalid_credentials')) return 'Wrong PIN or recovery code.';
    if (e.message.includes('invalid_nonce')) return 'That name is no longer available. Shuffle to try again.';
    if (e.message.includes('rate_limited')) return 'Too many attempts. Try again in a few minutes.';
    if (e.message.includes('pin_locked')) return 'Too many wrong tries — locked for an hour.';
    return e.message;
  }
  return String(e);
}
